import { ConversationId } from "PFCore/services/propilot";
import { IsoDate } from "PFTypes/date_time";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";

import {
  OutgoingMessage,
  ProPilotMessage,
  ProPilotMessageStatus,
  ProPilotStore
} from "./propilot_store_types";

const INITIAL_STATE = {
  conversations: {}
} satisfies Pick<ProPilotStore, "conversations">;

const PERSIST_CONFIG = {
  name: "propilot.conversations",
  version: 1,
  storage: createJSONStorage(() => sessionStorage),
  partialize: (state: ProPilotStore) => ({ conversations: state.conversations }),
  onRehydrateStorage: () => (state: ProPilotStore | undefined) => {
    if (state) {
      for (const conv of Object.values(state.conversations)) {
        conv.status = "idle";
        conv.pendingRetry = undefined;
      }
    }
  }
};

export const useProPilotStore = create<ProPilotStore>()(
  persist(
    immer((set) => ({
      ...INITIAL_STATE,
      actions: {
        createConversation: (id: ConversationId) =>
          set((state) => {
            if (state.conversations[id]) {
              return;
            }
            state.conversations[id] = {
              id,
              messages: [],
              status: "idle",
              pendingRetry: undefined,
              userInput: { value: "" }
            };
          }),

        appendMessage: (id: ConversationId, message: ProPilotMessage) =>
          set((state) => {
            if (!state.conversations[id]) {
              throw new Error(
                // eslint-disable-next-line max-len
                `[ProPilotStore] appendMessage: conversation "${id}" does not exist. Call createConversation first.`
              );
            }
            state.conversations[id].messages.push(message);
          }),

        setStatus: (id: ConversationId, status: ProPilotMessageStatus) =>
          set((state) => {
            if (!state.conversations[id]) {
              throw new Error(
                // eslint-disable-next-line max-len
                `[ProPilotStore] setStatus: conversation "${id}" does not exist. Call createConversation first.`
              );
            }
            state.conversations[id].status = status;
          }),

        setError: (id: ConversationId, reason: string) =>
          set((state) => {
            if (!state.conversations[id]) {
              throw new Error(
                // eslint-disable-next-line max-len
                `[ProPilotStore] setError: conversation "${id}" does not exist. Call createConversation first.`
              );
            }
            state.conversations[id].status = "error";
            state.conversations[id].messages.push({
              id: crypto.randomUUID(),
              author: "system",
              createdAt: new Date().toISOString() as IsoDate,
              content: { type: "system_error", text: reason, complete: true }
            });
          }),

        clearError: (id: ConversationId) =>
          set((state) => {
            if (!state.conversations[id]) {
              throw new Error(
                // eslint-disable-next-line max-len
                `[ProPilotStore] clearError: conversation "${id}" does not exist. Call createConversation first.`
              );
            }
            const { messages } = state.conversations[id];
            const last = messages[messages.length - 1];
            if (last && last.content.type === "system_error") {
              messages.pop();
            }
            state.conversations[id].status = "idle";
          }),

        setPendingRetry: (id: ConversationId, msg?: OutgoingMessage) =>
          set((state) => {
            if (!state.conversations[id]) {
              throw new Error(
                // eslint-disable-next-line max-len
                `[ProPilotStore] setPendingRetry: conversation "${id}" does not exist. Call createConversation first.`
              );
            }
            state.conversations[id].pendingRetry = msg?.text;
          }),

        setUserInputValue: (id: ConversationId, value: string) =>
          set((state) => {
            if (!state.conversations[id]) {
              throw new Error(`[ProPilotStore] setUserInputValue: unknown conversation "${id}"`);
            }
            state.conversations[id].userInput.value = value;
          }),

        clearConversation: (id: ConversationId) =>
          set((state) => {
            if (!state.conversations[id]) {
              throw new Error(
                // eslint-disable-next-line max-len
                `[ProPilotStore] clearConversation: conversation "${id}" does not exist. Call createConversation first.`
              );
            }
            state.conversations[id].messages = [];
            state.conversations[id].status = "idle";
            state.conversations[id].pendingRetry = undefined;
            state.conversations[id].userInput = { value: "" };
          }),

        destroyConversation: (id: ConversationId) =>
          set((state) => {
            delete state.conversations[id];
          }),

        reset: () => {
          set((state) => {
            Object.assign(state, INITIAL_STATE);
          });
          useProPilotStore.persist.clearStorage();
        }
      }
    })),
    PERSIST_CONFIG
  )
);

export const useProPilotStoreActions = () => useProPilotStore(useShallow((state) => state.actions));
